import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './TutorialOverlay.css';

type Lang = 'en' | 'es';

export interface TutorialStep {
  id: string;
  title: string;
  title_es: string;
  description: string;
  description_es: string;
  targetSelector?: string;
  route?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  sidebarAction?: 'open' | 'close';
  waitForSelector?: string;
  group: string;
  group_es: string;
  pmOnly?: boolean;
  userOnly?: boolean;
}

const TUTORIAL_STEPS: TutorialStep[] = [
  // ========== WELCOME ==========
  {
    id: 'welcome',
    title: 'Welcome to RAE Project Management',
    title_es: 'Bienvenido a RAE Project Management',
    description: 'This interactive tutorial will guide you through every feature of the project management platform. Use the arrows or keyboard (Left/Right) to navigate. You can exit at any time by pressing ESC or clicking the X button.',
    description_es: 'Este tutorial interactivo te guiará por cada función de la plataforma de gestión de proyectos. Usa las flechas o el teclado (Izquierda/Derecha) para navegar. Puedes salir en cualquier momento con ESC o el botón X.',
    position: 'center',
    group: 'Welcome',
    group_es: 'Bienvenida',
  },
  {
    id: 'welcome-roles',
    title: 'User Roles',
    title_es: 'Roles de Usuario',
    description: 'There are two main role types: Project Manager (PM) — full access to all sections including Dashboard, Workspace, Team, Reports, and Settings. Technicians/Helpers/Electricians/Mechanics — access only to My Tasks and Calendar. This tutorial covers the PM view.',
    description_es: 'Hay dos tipos de roles: Project Manager (PM) — acceso completo a Dashboard, Workspace, Team, Reports y Settings. Técnicos/Ayudantes/Electricistas/Mecánicos — acceso solo a My Tasks y Calendar. Este tutorial cubre la vista de PM.',
    position: 'center',
    group: 'Welcome',
    group_es: 'Bienvenida',
    pmOnly: true,
  },
  {
    id: 'welcome-roles',
    title: 'Your Role',
    title_es: 'Tu Rol',
    description: 'As a team member (Technician, Helper, Electrician, or Mechanic), you have access to My Tasks and the Calendar. My Tasks shows all tasks assigned to you, and the Calendar gives you a visual overview of your schedule. This tutorial will guide you through these sections.',
    description_es: 'Como miembro del equipo (Técnico, Ayudante, Electricista o Mecánico), tienes acceso a Mis Tareas y al Calendario. Mis Tareas muestra todas las tareas asignadas a ti, y el Calendario te da una vista visual de tu agenda. Este tutorial te guiará por estas secciones.',
    position: 'center',
    group: 'Welcome',
    group_es: 'Bienvenida',
    userOnly: true,
  },

  // ========== NAVIGATION ==========
  {
    id: 'sidebar-toggle',
    title: 'Menu Button',
    title_es: 'Botón de Menú',
    description: 'Click this hamburger button (☰) to open or close the sidebar navigation. The sidebar contains all sections of the application.',
    description_es: 'Haz clic en este botón (☰) para abrir o cerrar la barra lateral de navegación. La barra lateral contiene todas las secciones de la aplicación.',
    targetSelector: '.menu-button',
    position: 'right',
    route: '/',
    group: 'Navigation',
    group_es: 'Navegación',
  },
  {
    id: 'sidebar-overview',
    title: 'Sidebar Navigation',
    title_es: 'Barra Lateral',
    description: 'The sidebar shows your user profile at the top, followed by a project selector and navigation links: Dashboard, Workspace, My Tasks, Calendar, Team, Reports, and Settings. At the bottom you\'ll find the theme toggle and RAE logo.',
    description_es: 'La barra lateral muestra tu perfil en la parte superior, seguido del selector de proyecto y enlaces: Dashboard, Workspace, My Tasks, Calendar, Team, Reports y Settings. Abajo encontrarás el botón de tema y el logo de RAE.',
    targetSelector: '.sidebar',
    position: 'right',
    sidebarAction: 'open',
    group: 'Navigation',
    group_es: 'Navegación',
    pmOnly: true,
  },
  {
    id: 'sidebar-overview-user',
    title: 'Sidebar Navigation',
    title_es: 'Barra Lateral',
    description: 'The sidebar shows your user profile at the top, followed by a project selector and your navigation links: My Tasks and Calendar. At the bottom you\'ll find the theme toggle and RAE logo.',
    description_es: 'La barra lateral muestra tu perfil en la parte superior, seguido del selector de proyecto y tus enlaces de navegación: My Tasks y Calendar. Abajo encontrarás el botón de tema y el logo de RAE.',
    targetSelector: '.sidebar',
    position: 'right',
    sidebarAction: 'open',
    group: 'Navigation',
    group_es: 'Navegación',
    userOnly: true,
  },
  {
    id: 'sidebar-user-profile',
    title: 'User Profile Card',
    title_es: 'Tarjeta de Perfil',
    description: 'Shows your name, role badge (Project Manager, Technician, Helper, etc.), and avatar initial. The logout button is on the right side of this card. Click it to sign out.',
    description_es: 'Muestra tu nombre, insignia de rol (Project Manager, Technician, Helper, etc.) e inicial de avatar. El botón de cerrar sesión está a la derecha. Haz clic para salir.',
    targetSelector: '.user-profile-card',
    position: 'right',
    sidebarAction: 'open',
    group: 'Navigation',
    group_es: 'Navegación',
  },
  {
    id: 'sidebar-project',
    title: 'Project Selector',
    title_es: 'Selector de Proyecto',
    description: 'Click this expandable section to reveal the active project. The system supports multiple projects — the selected one determines all data across every page. The chevron (▸/▾) toggles the project list.',
    description_es: 'Haz clic en esta sección expandible para ver el proyecto activo. El sistema soporta múltiples proyectos — el seleccionado determina todos los datos en cada página. El chevron (▸/▾) despliega la lista.',
    targetSelector: '.nav-department:first-child',
    position: 'right',
    sidebarAction: 'open',
    group: 'Navigation',
    group_es: 'Navegación',
  },
  {
    id: 'sidebar-nav-links',
    title: 'Navigation Links',
    title_es: 'Enlaces de Navegación',
    description: 'These links take you to each section of the application. The active page is highlighted with an accent color. For non-PM users, only "My Tasks" and "Calendar" are visible.',
    description_es: 'Estos enlaces te llevan a cada sección. La página activa se resalta con color. Para usuarios no-PM, solo "My Tasks" y "Calendar" son visibles.',
    targetSelector: '.nav-list',
    position: 'right',
    sidebarAction: 'open',
    group: 'Navigation',
    group_es: 'Navegación',
  },
  {
    id: 'sidebar-settings-link',
    title: 'Settings',
    title_es: 'Configuración',
    description: 'Settings is in its own section at the bottom of the navigation. It provides access to Project Management, User Management, and Theme configuration. Only available to Project Managers.',
    description_es: 'Settings tiene su propia sección en la parte inferior de la navegación. Da acceso a Gestión de Proyectos, Gestión de Usuarios y configuración de Tema. Solo disponible para Project Managers.',
    targetSelector: '.nav-settings-section',
    position: 'right',
    sidebarAction: 'open',
    group: 'Navigation',
    group_es: 'Navegación',
    pmOnly: true,
  },
  {
    id: 'sidebar-theme',
    title: 'Theme Toggle',
    title_es: 'Cambio de Tema',
    description: 'Switch between Dark Mode (🌙) and Light Mode (☀️). Your preference is saved in local storage and persists across sessions. Click this button at any time to change the theme.',
    description_es: 'Cambia entre Modo Oscuro (🌙) y Modo Claro (☀️). Tu preferencia se guarda en almacenamiento local y persiste entre sesiones. Haz clic en cualquier momento para cambiar el tema.',
    targetSelector: '.header-theme-toggle',
    position: 'top',
    sidebarAction: 'open',
    group: 'Navigation',
    group_es: 'Navegación',
  },

  // ========== DASHBOARD ==========
  {
    id: 'dashboard-overview',
    title: 'Project Dashboard',
    title_es: 'Dashboard del Proyecto',
    description: 'The Dashboard gives you a bird\'s-eye view of the selected project. It shows the project header, KPI cards, status breakdown, team workload, and recent tasks. Only accessible to Project Managers.',
    description_es: 'El Dashboard te da una vista general del proyecto seleccionado. Muestra el encabezado del proyecto, tarjetas KPI, desglose de estados, carga de trabajo del equipo y tareas recientes. Solo accesible para Project Managers.',
    targetSelector: '.dashboard-title',
    position: 'bottom',
    route: '/',
    sidebarAction: 'close',
    group: 'Dashboard',
    group_es: 'Dashboard',
    pmOnly: true,
  },
  {
    id: 'dashboard-project-header',
    title: 'Project Header Card',
    title_es: 'Tarjeta de Encabezado',
    description: 'Shows the project name, status badge (Planning/In Progress/Completed/On Hold), client name, and an overall progress bar with percentage. This gives a quick snapshot of where the project stands.',
    description_es: 'Muestra el nombre del proyecto, insignia de estado (Planning/In Progress/Completed/On Hold), nombre del cliente y una barra de progreso general. Da un resumen rápido del estado del proyecto.',
    targetSelector: '.project-header-card',
    position: 'bottom',
    route: '/',
    group: 'Dashboard',
    group_es: 'Dashboard',
    pmOnly: true,
  },
  {
    id: 'dashboard-kpis',
    title: 'KPI Cards',
    title_es: 'Tarjetas KPI',
    description: 'Four key metrics at a glance: Total Tasks (all sub-elements), In Progress (actively being worked), Completed (finished tasks), and Issues (tasks with problems). Each card shows a count and colored icon. Click a card to navigate to the relevant view.',
    description_es: 'Cuatro métricas clave: Total Tasks (todos los sub-elementos), In Progress (en trabajo activo), Completed (terminadas) e Issues (con problemas). Cada tarjeta muestra un conteo e ícono coloreado. Haz clic para navegar a la vista relevante.',
    targetSelector: '.priority-grid',
    position: 'bottom',
    route: '/',
    group: 'Dashboard',
    group_es: 'Dashboard',
    pmOnly: true,
  },
  {
    id: 'dashboard-status-breakdown',
    title: 'Status Breakdown',
    title_es: 'Desglose de Estados',
    description: 'A detailed bar chart showing how many tasks are in each status: Assigned (gray), Started (blue), In Progress (cyan), Paused (amber), Finished (green), and Issues (red). Each row shows a progress bar and count.',
    description_es: 'Un gráfico de barras detallado mostrando cuántas tareas hay en cada estado: Assigned (gris), Started (azul), In Progress (cyan), Paused (ámbar), Finished (verde) e Issues (rojo). Cada fila muestra una barra de progreso y conteo.',
    targetSelector: '.dashboard-card:first-child',
    position: 'bottom',
    route: '/',
    group: 'Dashboard',
    group_es: 'Dashboard',
    pmOnly: true,
  },
  {
    id: 'dashboard-team-workload',
    title: 'Team Workload',
    title_es: 'Carga de Trabajo del Equipo',
    description: 'Shows each team member\'s avatar, name, task count (completed / total), and a progress bar. Click "View Team →" to navigate to the full Team Overview page. Quickly spot who is overloaded or underutilized.',
    description_es: 'Muestra el avatar de cada miembro, nombre, conteo de tareas (completadas / total) y barra de progreso. Haz clic en "View Team →" para navegar a la página completa del equipo. Detecta rápidamente quién está sobrecargado o subutilizado.',
    targetSelector: '.team-workload-list',
    position: 'top',
    route: '/',
    group: 'Dashboard',
    group_es: 'Dashboard',
    pmOnly: true,
  },
  {
    id: 'dashboard-recent-tasks',
    title: 'Recent Tasks Table',
    title_es: 'Tabla de Tareas Recientes',
    description: 'A table of recently updated tasks showing: Task Code, Name, Status badge, Assigned personnel, Estimated hours, and Last Updated date. Click any row to navigate directly to that task\'s detail page.',
    description_es: 'Una tabla de tareas actualizadas recientemente mostrando: Código, Nombre, Estado, Personal asignado, Horas estimadas y Fecha de actualización. Haz clic en cualquier fila para ir al detalle de la tarea.',
    targetSelector: '.table-wrapper',
    position: 'top',
    route: '/',
    group: 'Dashboard',
    group_es: 'Dashboard',
    pmOnly: true,
  },

  // ========== WORKSPACE ==========
  {
    id: 'workspace-overview',
    title: 'Project Workspace',
    title_es: 'Espacio de Trabajo',
    description: 'The Workspace is the main work area where the PM manages the entire project hierarchy. It displays a hierarchical table: Zones → Sections → Elements → Sub-Elements (tasks). This is where all CRUD operations happen.',
    description_es: 'El Workspace es el área de trabajo principal donde el PM gestiona toda la jerarquía del proyecto. Muestra una tabla jerárquica: Zonas → Secciones → Elementos → Sub-Elementos (tareas). Aquí se realizan todas las operaciones CRUD.',
    targetSelector: '.workspace-title',
    position: 'bottom',
    route: '/workspace',
    sidebarAction: 'close',
    group: 'Workspace',
    group_es: 'Espacio de Trabajo',
    pmOnly: true,
  },
  {
    id: 'workspace-toolbar',
    title: 'Workspace Toolbar',
    title_es: 'Barra de Herramientas',
    description: 'The toolbar has: a Search input (filters by name across all levels), a Status dropdown (filter by task status), Expand All / Collapse All buttons, and an "+ Add Zone" button (PM only) to create new top-level zones.',
    description_es: 'La barra de herramientas tiene: un campo de Búsqueda (filtra por nombre en todos los niveles), un desplegable de Estado, botones Expand All / Collapse All, y un botón "+ Add Zone" (solo PM) para crear zonas nuevas.',
    targetSelector: '.ws-toolbar',
    position: 'bottom',
    route: '/workspace',
    group: 'Workspace',
    group_es: 'Espacio de Trabajo',
    pmOnly: true,
  },
  {
    id: 'workspace-search',
    title: 'Search',
    title_es: 'Búsqueda',
    description: 'Type any text to instantly filter the hierarchy. Matching items and their parents are shown — for example, searching for a sub-element name will also show its Zone, Section, and Element. Clear the search to see everything.',
    description_es: 'Escribe cualquier texto para filtrar la jerarquía al instante. Los elementos que coinciden y sus padres se muestran — por ejemplo, buscar un sub-elemento también muestra su Zona, Sección y Elemento. Limpia la búsqueda para ver todo.',
    targetSelector: '.ws-search-input',
    position: 'bottom',
    route: '/workspace',
    group: 'Workspace',
    group_es: 'Espacio de Trabajo',
    pmOnly: true,
  },
  {
    id: 'workspace-filter',
    title: 'Status Filter',
    title_es: 'Filtro de Estado',
    description: 'Filter the workspace by task status: All Statuses, Assigned, Started, In Progress, Paused, Finished, or Issues. Only sub-elements (tasks) are filtered — parent rows remain visible if they contain matching tasks.',
    description_es: 'Filtra el workspace por estado de tarea: All Statuses, Assigned, Started, In Progress, Paused, Finished o Issues. Solo se filtran los sub-elementos — las filas padre siguen visibles si contienen tareas que coinciden.',
    targetSelector: '.ws-filter-select',
    position: 'bottom',
    route: '/workspace',
    group: 'Workspace',
    group_es: 'Espacio de Trabajo',
    pmOnly: true,
  },
  {
    id: 'workspace-table',
    title: 'Hierarchical Table',
    title_es: 'Tabla Jerárquica',
    description: 'The table displays columns: Name, Code, Actions (CRUD), Status/Progress, Priority, Assigned, Est/Actual Hours, Dates, and Attachments/Comments count. Each hierarchy level has a different indent and styling.',
    description_es: 'La tabla muestra columnas: Nombre, Código, Acciones (CRUD), Estado/Progreso, Prioridad, Asignados, Horas Est/Reales, Fechas y conteo de Archivos/Comentarios. Cada nivel jerárquico tiene una indentación y estilo diferente.',
    targetSelector: '.ws-table',
    position: 'top',
    route: '/workspace',
    group: 'Workspace',
    group_es: 'Espacio de Trabajo',
    pmOnly: true,
  },

  // ========== WORKSPACE HIERARCHY ==========
  {
    id: 'ws-hierarchy-zone',
    title: 'Zones (Level 1)',
    title_es: 'Zonas (Nivel 1)',
    description: 'Zones are the top-level containers (e.g., "Ground Floor", "Building A"). They show a folder icon, the zone name, a child count badge, and an aggregated progress bar. Let\'s expand this zone to see what\'s inside...',
    description_es: 'Las Zonas son los contenedores de nivel superior (ej., "Planta Baja", "Edificio A"). Muestran un ícono de carpeta, el nombre, un conteo de hijos y una barra de progreso agregada. Expandamos esta zona para ver qué hay dentro...',
    targetSelector: '.ws-row-zone',
    position: 'bottom',
    route: '/workspace',
    group: 'Hierarchy',
    group_es: 'Jerarquía',
    pmOnly: true,
  },
  {
    id: 'ws-hierarchy-section',
    title: 'Sections (Level 2)',
    title_es: 'Secciones (Nivel 2)',
    description: 'The zone has been expanded to reveal its Sections. Sections organize work within a Zone (e.g., "Electrical", "Plumbing"). They display a name, code, aggregated progress, and child count. Let\'s expand a section next...',
    description_es: 'La zona se ha expandido para revelar sus Secciones. Las Secciones organizan el trabajo dentro de una Zona (ej., "Eléctrico", "Plomería"). Muestran nombre, código, progreso agregado y conteo de hijos. Expandamos una sección...',
    targetSelector: '.ws-row-section',
    position: 'bottom',
    route: '/workspace',
    waitForSelector: '.ws-row-section',
    group: 'Hierarchy',
    group_es: 'Jerarquía',
    pmOnly: true,
  },
  {
    id: 'ws-hierarchy-element',
    title: 'Elements (Level 3)',
    title_es: 'Elementos (Nivel 3)',
    description: 'The section has been expanded to reveal its Elements. Elements represent specific work packages (e.g., "Panel Installation", "Cable Routing"). They show name, code, aggregated progress, and child count. Expanding to see the tasks...',
    description_es: 'La sección se ha expandido para revelar sus Elementos. Los Elementos representan paquetes de trabajo específicos (ej., "Instalación de Panel", "Cableado"). Muestran nombre, código, progreso agregado y conteo de hijos. Expandiendo para ver las tareas...',
    targetSelector: '.ws-row-element',
    position: 'bottom',
    route: '/workspace',
    waitForSelector: '.ws-row-element',
    group: 'Hierarchy',
    group_es: 'Jerarquía',
    pmOnly: true,
  },
  {
    id: 'ws-hierarchy-subelement',
    title: 'Sub-Elements / Tasks (Level 4)',
    title_es: 'Sub-Elementos / Tareas (Nivel 4)',
    description: 'The element has been expanded to reveal Sub-Elements — the actual leaf-level tasks assigned to technicians. They show: Status badge, Priority dot, Assigned users (avatar chips), Estimated/Actual hours, Date range, and Attachment/Comment counts. Click any row to open its detail page.',
    description_es: 'El elemento se ha expandido para revelar Sub-Elementos — las tareas reales asignadas a técnicos. Muestran: Estado, Prioridad, Usuarios asignados (chips de avatar), Horas estimadas/reales, Rango de fechas y conteo de Archivos/Comentarios. Haz clic en cualquier fila para abrir su detalle.',
    targetSelector: '.ws-row-subelement',
    position: 'bottom',
    route: '/workspace',
    waitForSelector: '.ws-row-subelement',
    group: 'Hierarchy',
    group_es: 'Jerarquía',
    pmOnly: true,
  },

  // ========== WORKSPACE CRUD ==========
  {
    id: 'ws-crud-overview',
    title: 'CRUD Operations',
    title_es: 'Operaciones CRUD',
    description: 'Now let\'s expand all levels to see the full project. Project Managers can Create, Read, Update, and Delete at every hierarchy level. Each row shows action icons on hover: "+" (add child), "✎" (edit), and "🗑" (delete). All changes sync in real-time to the database.',
    description_es: 'Ahora expandamos todos los niveles para ver el proyecto completo. Los Project Managers pueden Crear, Leer, Actualizar y Eliminar en cada nivel. Cada fila muestra íconos de acción al pasar el mouse: "+" (agregar hijo), "✎" (editar) y "🗑" (eliminar). Todos los cambios se sincronizan en tiempo real.',
    targetSelector: '.ws-table',
    position: 'top',
    route: '/workspace',
    group: 'Workspace CRUD',
    group_es: 'CRUD del Workspace',
    pmOnly: true,
  },
  {
    id: 'ws-crud-add-zone',
    title: 'Add Zone',
    title_es: 'Agregar Zona',
    description: 'Click the "+ Add Zone" button in the toolbar. An inline form row appears at the bottom of the table where you type the zone name and code, then click ✓ to save or ✕ to cancel. The zone is immediately created in the database.',
    description_es: 'Haz clic en el botón "+ Add Zone" en la barra de herramientas. Aparece un formulario en línea al final de la tabla donde escribes el nombre y código de la zona, luego clic en ✓ para guardar o ✕ para cancelar. La zona se crea inmediatamente en la base de datos.',
    targetSelector: '.add-btn',
    position: 'bottom',
    route: '/workspace',
    group: 'Workspace CRUD',
    group_es: 'CRUD del Workspace',
    pmOnly: true,
  },
  {
    id: 'ws-crud-add-child',
    title: 'Add Child Item',
    title_es: 'Agregar Elemento Hijo',
    description: 'Hover over any Zone, Section, or Element row and click the "+" button that appears. An inline form appears as a child row where you type the name and code. For sub-elements (tasks), you can also set priority, dates, and hours. Click ✓ to save.',
    description_es: 'Pasa el mouse sobre cualquier fila de Zona, Sección o Elemento y haz clic en el botón "+". Aparece un formulario en línea como fila hija donde escribes el nombre y código. Para sub-elementos (tareas), también puedes establecer prioridad, fechas y horas. Clic en ✓ para guardar.',
    targetSelector: '.ws-crud-actions',
    position: 'left',
    route: '/workspace',
    group: 'Workspace CRUD',
    group_es: 'CRUD del Workspace',
    pmOnly: true,
  },
  {
    id: 'ws-crud-edit',
    title: 'Edit Item',
    title_es: 'Editar Elemento',
    description: 'Click the pencil icon (✎) on any row. The name and code fields become editable inline inputs. For sub-elements, you can also edit priority, assigned personnel, dates, and hours. Click ✓ to save changes or ✕ to cancel.',
    description_es: 'Haz clic en el ícono de lápiz (✎) en cualquier fila. Los campos de nombre y código se convierten en inputs editables. Para sub-elementos, también puedes editar prioridad, personal asignado, fechas y horas. Clic en ✓ para guardar o ✕ para cancelar.',
    position: 'center',
    route: '/workspace',
    group: 'Workspace CRUD',
    group_es: 'CRUD del Workspace',
    pmOnly: true,
  },
  {
    id: 'ws-crud-delete',
    title: 'Delete Item',
    title_es: 'Eliminar Elemento',
    description: 'Click the trash icon (🗑) on any row. A confirmation prompt appears with ✓ (confirm) and ✕ (cancel) buttons. Deleting a parent item (Zone, Section, Element) also deletes all children. This action is permanent.',
    description_es: 'Haz clic en el ícono de basura (🗑) en cualquier fila. Aparece una confirmación con botones ✓ (confirmar) y ✕ (cancelar). Eliminar un padre (Zona, Sección, Elemento) también elimina todos los hijos. Esta acción es permanente.',
    position: 'center',
    route: '/workspace',
    group: 'Workspace CRUD',
    group_es: 'CRUD del Workspace',
    pmOnly: true,
  },
  {
    id: 'ws-columns',
    title: 'Table Columns',
    title_es: 'Columnas de la Tabla',
    description: 'The workspace table columns: Name (with indent), Code (monospace), Actions (CRUD icons), Status (badge or progress bar), Priority (colored dot + label), Assigned (avatar chips), Hours (estimated / actual — red if over), Dates (start–end range), Attachments (📎 count + 💬 count).',
    description_es: 'Columnas de la tabla: Nombre (con indentación), Código (monospace), Acciones (íconos CRUD), Estado (badge o barra), Prioridad (punto coloreado + etiqueta), Asignados (chips de avatar), Horas (estimadas / reales — rojo si excede), Fechas (rango inicio–fin), Archivos (📎 conteo + 💬 conteo).',
    position: 'center',
    route: '/workspace',
    group: 'Workspace CRUD',
    group_es: 'CRUD del Workspace',
    pmOnly: true,
  },

  // ========== TASK DETAIL ==========
  {
    id: 'task-detail-enter',
    title: 'Entering Task Detail',
    title_es: 'Entrando al Detalle de Tarea',
    description: 'Let\'s open a task to explore its detail page. Clicking any sub-element row in the Workspace, My Tasks, or Calendar navigates to the Task Detail — the central hub for task management, status transitions, time tracking, and team collaboration.',
    description_es: 'Abramos una tarea para explorar su página de detalle. Al hacer clic en cualquier fila de sub-elemento en Workspace, My Tasks o Calendar se navega al Detalle de Tarea — el centro para gestión de tareas, transiciones de estado, seguimiento de tiempo y colaboración.',
    waitForSelector: '.detail-page',
    targetSelector: '.detail-header',
    position: 'bottom',
    sidebarAction: 'close',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-header',
    title: 'Task Header',
    title_es: 'Encabezado de Tarea',
    description: 'The header shows a "← Back" button (returns to the previous page), the task code (e.g., Z1-S1-E1-T1), and the task name. The back button remembers which section you came from (Workspace, My Tasks, or Calendar).',
    description_es: 'El encabezado muestra un botón "← Back" (regresa a la página anterior), el código de tarea (ej., Z1-S1-E1-T1) y el nombre de la tarea. El botón de regreso recuerda de qué sección viniste.',
    targetSelector: '.detail-header',
    position: 'bottom',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-status-bar',
    title: 'Status & Priority Bar',
    title_es: 'Barra de Estado y Prioridad',
    description: 'This row shows the current Status badge (colored by state), a clickable Priority badge (dropdown to change), the Date range (with "Overdue" badge if past due), and the Estimated vs Actual hours summary.',
    description_es: 'Esta fila muestra el badge de Estado actual (coloreado por estado), un badge de Prioridad clickeable (desplegable para cambiar), el Rango de fechas (con badge "Overdue" si está vencida) y el resumen de Horas estimadas vs reales.',
    targetSelector: '.status-row-detail',
    position: 'bottom',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-action-buttons',
    title: 'Status Transition Menu',
    title_es: 'Botones de Acción de Estado',
    description: 'These buttons control task status transitions. Available actions depend on the current status: ASSIGNED → Start, STARTED → Begin Work, IN_PROGRESS → Pause / Report Issue / Finish, PAUSED → Resume, ISSUES → Resume Work. Only valid next-state transitions are shown.',
    description_es: 'Estos botones controlan las transiciones de estado. Las acciones disponibles dependen del estado actual: ASSIGNED → Start, STARTED → Begin Work, IN_PROGRESS → Pause / Report Issue / Finish, PAUSED → Resume, ISSUES → Resume Work. Solo se muestran transiciones válidas.',
    targetSelector: '.status-selector-dropdown',
    position: 'bottom',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-status-machine',
    title: 'Status State Machine',
    title_es: 'Máquina de Estados',
    description: 'Tasks follow this flow: ASSIGNED → STARTED → IN_PROGRESS ↔ PAUSED → FINISHED. From any working state, you can switch to ISSUES, and from ISSUES back to IN_PROGRESS. Each transition is logged in the Activity Feed with a timestamp and user info.',
    description_es: 'Las tareas siguen este flujo: ASSIGNED → STARTED → IN_PROGRESS ↔ PAUSED → FINISHED. Desde cualquier estado de trabajo, puedes cambiar a ISSUES, y de ISSUES volver a IN_PROGRESS. Cada transición se registra en el Feed de Actividad con fecha y usuario.',
    position: 'center',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-status-modal',
    title: 'Status Change Modal',
    title_es: 'Modal de Cambio de Estado',
    description: 'When transitioning to FINISHED or ISSUES, a modal pops up requiring: a comment explaining the change, and optional photo attachments (for documenting completed work or problems). The comment is mandatory — the save button is disabled until text is entered.',
    description_es: 'Al transicionar a FINISHED o ISSUES, aparece un modal que requiere: un comentario explicando el cambio, y fotos adjuntas opcionales (para documentar trabajo completado o problemas). El comentario es obligatorio — el botón de guardar se deshabilita hasta que se escriba texto.',
    position: 'center',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-time-tracking',
    title: 'Time Tracking',
    title_es: 'Seguimiento de Tiempo',
    description: 'Tasks have automatic time tracking. Transitioning to IN_PROGRESS or STARTED starts a timer. PAUSED or ISSUES stops it. FINISHED stops it permanently. Elapsed time accumulates across all sessions as "Actual Hours" shown here.',
    description_es: 'Las tareas tienen seguimiento de tiempo automático. Al transicionar a IN_PROGRESS o STARTED se inicia un temporizador. PAUSED o ISSUES lo detiene. FINISHED lo detiene permanentemente. El tiempo se acumula entre sesiones como "Actual Hours".',
    targetSelector: '.total-amount-box',
    position: 'bottom',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-priority',
    title: 'Priority Picker',
    title_es: 'Selector de Prioridad',
    description: 'Click the priority badge to open a dropdown with options: Low (green), Medium (amber), High (orange), and Critical (red). The selected priority is immediately saved and reflected in the Workspace table and task cards throughout the app.',
    description_es: 'Haz clic en el badge de prioridad para abrir un desplegable con opciones: Low (verde), Medium (ámbar), High (naranja) y Critical (rojo). La prioridad seleccionada se guarda inmediatamente y se refleja en la tabla del Workspace y tarjetas de toda la app.',
    targetSelector: '.priority-picker-wrap',
    position: 'bottom',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-info-cards',
    title: 'Information Cards',
    title_es: 'Tarjetas de Información',
    description: 'Below the action area, three cards display task data: Task Information (description, location path, dates, hours), Assigned Personnel (team members on this task), and Attachments (uploaded files). Each card is editable by the Project Manager via the pencil icon.',
    description_es: 'Debajo del área de acciones, tres tarjetas muestran datos de la tarea: Task Information (descripción, ruta, fechas, horas), Assigned Personnel (miembros del equipo) y Attachments (archivos). Cada tarjeta es editable por el PM mediante el ícono de lápiz.',
    targetSelector: '.detail-grid',
    position: 'top',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-description',
    title: 'Task Information Card',
    title_es: 'Tarjeta de Información',
    description: 'This card shows the task description (editable via pencil icon), the full hierarchy path (Zone → Section → Element), and editable fields: Start Date, End Date, and Estimated Hours. Click the pencil icon next to any field to modify it.',
    description_es: 'Esta tarjeta muestra la descripción de la tarea (editable con el ícono de lápiz), la ruta jerárquica completa (Zona → Sección → Elemento) y campos editables: Fecha Inicio, Fecha Fin y Horas Estimadas. Haz clic en el lápiz para modificar.',
    targetSelector: '.detail-grid .detail-card:nth-child(1)',
    position: 'right',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-assigned',
    title: 'Assigned Personnel',
    title_es: 'Personal Asignado',
    description: 'This card shows who is assigned to this task. PMs can click Edit to open a checkbox list of all team members and toggle assignments. Multiple people can work on the same task. Each assignee appears as an avatar chip with their initials.',
    description_es: 'Esta tarjeta muestra quién está asignado a esta tarea. Los PMs pueden hacer clic en Editar para abrir una lista de checkboxes de todos los miembros y alternar asignaciones. Varias personas pueden trabajar en la misma tarea. Cada asignado aparece como chip de avatar.',
    targetSelector: '.detail-grid .detail-card:nth-child(3)',
    position: 'left',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-attachments',
    title: 'Attachments',
    title_es: 'Archivos Adjuntos',
    description: 'Cloud file storage. Supports drag-and-drop or click-to-browse. Shows thumbnail previews for images, PDF icon for PDFs, generic icon for other types. Click a thumbnail to preview in a modal; click ✕ to remove. Max 10 MB per file.',
    description_es: 'Almacenamiento de archivos en la nube. Soporta arrastrar-y-soltar o clic para buscar. Muestra miniaturas para imágenes, ícono PDF para PDFs, ícono genérico para otros tipos. Clic en miniatura para previsualizar; clic en ✕ para eliminar. Máx 10 MB por archivo.',
    targetSelector: '.detail-grid + .detail-card.full-width + .detail-card.full-width',
    position: 'top',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-activity-feed',
    title: 'Activity Feed',
    title_es: 'Feed de Actividad',
    description: 'A chronological feed of all task activity: Status Changes (old → new status with timestamp, comment and photos) and User Comments (text with optional photo attachments). The most recent activity appears at the top. All entries update in real-time.',
    description_es: 'Un feed cronológico de toda la actividad de la tarea: Cambios de Estado (estado anterior → nuevo con fecha, comentario y fotos) y Comentarios de Usuario (texto con fotos opcionales). La actividad más reciente aparece arriba. Todo se actualiza en tiempo real.',
    targetSelector: '.task-comments-list',
    position: 'top',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },
  {
    id: 'task-detail-comments',
    title: 'Adding Comments',
    title_es: 'Agregar Comentarios',
    description: 'At the bottom of the feed, type a message and click Send (or press Enter). Attach photos using the camera icon — preview thumbnails appear before sending. Comments are stored in the database and appear in real-time for all team members viewing this task.',
    description_es: 'Al final del feed, escribe un mensaje y haz clic en Enviar (o presiona Enter). Adjunta fotos usando el ícono de cámara — las miniaturas aparecen antes de enviar. Los comentarios se guardan en la base de datos y aparecen en tiempo real para todo el equipo.',
    targetSelector: '.comment-input-area',
    position: 'top',
    group: 'Task Detail',
    group_es: 'Detalle de Tarea',
  },

  // ========== MY TASKS ==========
  {
    id: 'mytasks-overview',
    title: 'My Tasks',
    title_es: 'Mis Tareas',
    description: 'Shows only tasks assigned to the currently logged-in user. This is the main page for Technicians (non-PM roles). It shows quick stats at the top and supports two views: List (default) and Calendar.',
    description_es: 'Muestra solo las tareas asignadas al usuario actual. Esta es la página principal para Técnicos (roles no-PM). Muestra estadísticas rápidas arriba y soporta dos vistas: Lista (por defecto) y Calendario.',
    targetSelector: '.mytasks-title',
    position: 'bottom',
    route: '/my-tasks',
    sidebarAction: 'close',
    group: 'My Tasks',
    group_es: 'Mis Tareas',
  },
  {
    id: 'mytasks-stats',
    title: 'Quick Stats',
    title_es: 'Estadísticas Rápidas',
    description: 'Three stat counters at the top: Total (all assigned tasks — green), In Progress (actively working — amber), and Issues (tasks with problems — red). These update in real-time as task statuses change.',
    description_es: 'Tres contadores de estadísticas arriba: Total (todas las tareas asignadas — verde), In Progress (en trabajo activo — ámbar) e Issues (tareas con problemas — rojo). Se actualizan en tiempo real al cambiar los estados.',
    targetSelector: '.mytasks-stats',
    position: 'bottom',
    route: '/my-tasks',
    group: 'My Tasks',
    group_es: 'Mis Tareas',
  },
  {
    id: 'mytasks-view-toggle',
    title: 'List / Calendar Toggle',
    title_es: 'Alternar Lista / Calendario',
    description: 'Switch between two views: List View (tasks grouped by week and day with detailed cards) and Calendar View (a monthly grid showing tasks on their due dates). The active view is highlighted.',
    description_es: 'Cambia entre dos vistas: Vista de Lista (tareas agrupadas por semana y día con tarjetas detalladas) y Vista de Calendario (una cuadrícula mensual mostrando tareas en sus fechas). La vista activa se resalta.',
    targetSelector: '.mytasks-view-toggle',
    position: 'bottom',
    route: '/my-tasks',
    group: 'My Tasks',
    group_es: 'Mis Tareas',
  },
  {
    id: 'mytasks-filter',
    title: 'Status Filter',
    title_es: 'Filtro de Estado',
    description: 'Filter your tasks by status using the dropdown: All Statuses, Assigned, Started, In Progress, Paused, Finished, or Issues. The filter applies to both List and Calendar views.',
    description_es: 'Filtra tus tareas por estado usando el desplegable: All Statuses, Assigned, Started, In Progress, Paused, Finished o Issues. El filtro aplica a ambas vistas Lista y Calendario.',
    targetSelector: '.ws-filter-select',
    position: 'bottom',
    route: '/my-tasks',
    group: 'My Tasks',
    group_es: 'Mis Tareas',
  },
  {
    id: 'mytasks-list-view',
    title: 'List View',
    title_es: 'Vista de Lista',
    description: 'Tasks are grouped by week (expandable), then by day within each week. Each task card shows: Code, Name, Status badge, Zone → Section → Element path, Priority, Hours, Due date (red if overdue), and Co-assignees. "This Week" is highlighted. Click a card to open the task detail.',
    description_es: 'Las tareas se agrupan por semana (expandible), luego por día. Cada tarjeta muestra: Código, Nombre, Estado, ruta Zona → Sección → Elemento, Prioridad, Horas, Fecha límite (rojo si vencida) y Co-asignados. "This Week" se resalta. Clic en una tarjeta para abrir el detalle.',
    targetSelector: '.mytasks-list',
    position: 'top',
    route: '/my-tasks',
    group: 'My Tasks',
    group_es: 'Mis Tareas',
  },
  {
    id: 'mytasks-calendar-view',
    title: 'Calendar View',
    title_es: 'Vista de Calendario',
    description: 'A monthly grid showing task chips on their end dates. Navigate months with ‹/› arrows. Today is highlighted with a blue circle. Task chips are color-coded by status. Click a chip to open the task detail. Days with many tasks show "+X more".',
    description_es: 'Una cuadrícula mensual mostrando chips de tareas en sus fechas de fin. Navega meses con flechas ‹/›. Hoy se resalta con un círculo azul. Los chips están coloreados por estado. Clic en un chip para abrir el detalle. Días con muchas tareas muestran "+X más".',
    position: 'center',
    route: '/my-tasks',
    group: 'My Tasks',
    group_es: 'Mis Tareas',
  },

  // ========== CALENDAR ==========
  {
    id: 'calendar-overview',
    title: 'Project Calendar',
    title_es: 'Calendario del Proyecto',
    description: 'The Calendar page shows ALL project tasks (not just yours) in two views: Monthly Calendar and Gantt Chart. It includes a status filter and task count.',
    description_es: 'La página de Calendario muestra TODAS las tareas del proyecto (no solo las tuyas) en dos vistas: Calendario Mensual y Gráfico Gantt. Incluye filtro de estado y conteo de tareas.',
    targetSelector: '.calendar-page-title',
    position: 'bottom',
    route: '/calendar',
    sidebarAction: 'close',
    group: 'Calendar',
    group_es: 'Calendario',
    pmOnly: true,
  },
  {
    id: 'calendar-view-toggle',
    title: 'Calendar / Gantt Toggle',
    title_es: 'Alternar Calendario / Gantt',
    description: 'Switch between Calendar view (monthly grid with task chips) and Gantt view (horizontal timeline bars). The active view button is highlighted with an accent color.',
    description_es: 'Cambia entre vista de Calendario (cuadrícula mensual con chips de tareas) y vista Gantt (barras de línea de tiempo horizontal). El botón de vista activa se resalta con color.',
    targetSelector: '.calendar-page-view-toggle',
    position: 'bottom',
    route: '/calendar',
    group: 'Calendar',
    group_es: 'Calendario',
    pmOnly: true,
  },
  {
    id: 'calendar-month-view',
    title: 'Monthly Calendar',
    title_es: 'Calendario Mensual',
    description: 'A 7-column grid (Mon–Sun) showing all project tasks on their date range. Each task appears as a colored chip with the task code and name. Click any chip to open the task detail. Navigate months with ‹/› arrows. Today is highlighted.',
    description_es: 'Una cuadrícula de 7 columnas (Lun–Dom) mostrando todas las tareas en su rango de fechas. Cada tarea aparece como chip coloreado con código y nombre. Clic en cualquier chip para abrir el detalle. Navega meses con flechas ‹/›. Hoy se resalta.',
    targetSelector: '.calendar-page-calendar',
    position: 'top',
    route: '/calendar',
    group: 'Calendar',
    group_es: 'Calendario',
    pmOnly: true,
  },
  {
    id: 'calendar-gantt',
    title: 'Gantt Chart',
    title_es: 'Gráfico Gantt',
    description: 'Switching to the Gantt view now. This horizontal timeline shows task bars across a date axis. Each bar represents a task\'s start-to-end date range and is color-coded by status. It provides a visual overview of scheduling, overlaps, and gaps.',
    description_es: 'Cambiando a la vista Gantt ahora. Esta línea de tiempo horizontal muestra barras de tareas en un eje de fechas. Cada barra representa el rango inicio-fin de una tarea y está coloreada por estado. Proporciona una vista general de programación, solapamientos y huecos.',
    targetSelector: '.calendar-page-gantt',
    position: 'top',
    route: '/calendar',
    waitForSelector: '.calendar-page-gantt',
    group: 'Calendar',
    group_es: 'Calendario',
    pmOnly: true,
  },
  {
    id: 'calendar-gantt-expanded',
    title: 'Gantt — Expanded View',
    title_es: 'Gantt — Vista Expandida',
    description: 'The Gantt chart is now fully expanded showing all tasks. Each bar is clickable — it navigates to the task detail page. The timeline axis shows dates, and tasks are grouped by their zone/section hierarchy. Use this view to spot scheduling conflicts and resource gaps.',
    description_es: 'El gráfico Gantt ahora está completamente expandido mostrando todas las tareas. Cada barra es clickeable — navega al detalle de la tarea. El eje muestra fechas y las tareas se agrupan por jerarquía zona/sección. Usa esta vista para detectar conflictos de programación.',
    targetSelector: '.calendar-page-gantt',
    position: 'top',
    route: '/calendar',
    group: 'Calendar',
    group_es: 'Calendario',
    pmOnly: true,
  },

  // ========== TEAM ==========
  {
    id: 'team-overview',
    title: 'Team Overview',
    title_es: 'Vista General del Equipo',
    description: 'The Team page shows all project members with their workload and performance stats. It includes summary cards at the top and individual member cards in a grid. Only accessible to Project Managers.',
    description_es: 'La página de Equipo muestra todos los miembros del proyecto con su carga de trabajo y estadísticas de rendimiento. Incluye tarjetas de resumen arriba y tarjetas individuales en una cuadrícula. Solo accesible para Project Managers.',
    targetSelector: '.team-title',
    position: 'bottom',
    route: '/team',
    sidebarAction: 'close',
    group: 'Team',
    group_es: 'Equipo',
    pmOnly: true,
  },
  {
    id: 'team-summary',
    title: 'Summary Cards',
    title_es: 'Tarjetas de Resumen',
    description: 'Three summary stats: Total Members (team size), Avg. Progress (average completion percentage), and Issues (total tasks with problems). These give a quick team health check.',
    description_es: 'Tres estadísticas de resumen: Total Members (tamaño del equipo), Avg. Progress (porcentaje promedio de finalización) e Issues (total de tareas con problemas). Dan un chequeo rápido de salud del equipo.',
    targetSelector: '.team-summary',
    position: 'bottom',
    route: '/team',
    group: 'Team',
    group_es: 'Equipo',
    pmOnly: true,
  },
  {
    id: 'team-member-cards',
    title: 'Member Cards',
    title_es: 'Tarjetas de Miembros',
    description: 'Each card shows: avatar with initials, name, email, a stacked workload bar (color-coded by status — green for finished, cyan for in-progress, amber for paused, red for issues), individual stats (Total Tasks, Finished, In Progress, Issues, Actual Hours), and a completion percentage bar.',
    description_es: 'Cada tarjeta muestra: avatar con iniciales, nombre, email, barra de carga apilada (coloreada por estado — verde para finished, cyan para in-progress, ámbar para paused, rojo para issues), estadísticas individuales (Total, Finished, In Progress, Issues, Actual Hours) y barra de porcentaje.',
    targetSelector: '.team-grid',
    position: 'top',
    route: '/team',
    group: 'Team',
    group_es: 'Equipo',
    pmOnly: true,
  },
  {
    id: 'team-workload-bar',
    title: 'Workload Bar',
    title_es: 'Barra de Carga de Trabajo',
    description: 'Each member card has a stacked horizontal bar showing the proportion of tasks in each status. The segments are: Finished (green), In Progress (cyan), Started (blue), Assigned (gray), Paused (amber), and Issues (red). This visually shows workload distribution.',
    description_es: 'Cada tarjeta tiene una barra horizontal apilada mostrando la proporción de tareas en cada estado. Los segmentos son: Finished (verde), In Progress (cyan), Started (azul), Assigned (gris), Paused (ámbar) e Issues (rojo). Esto muestra visualmente la distribución de carga.',
    targetSelector: '.team-workload-bar',
    position: 'top',
    route: '/team',
    group: 'Team',
    group_es: 'Equipo',
    pmOnly: true,
  },

  // ========== REPORTS ==========
  {
    id: 'reports-overview',
    title: 'Reports',
    title_es: 'Reportes',
    description: 'The Reports page provides analytical insights into the project\'s progress. It contains KPI cards, status distribution, zone performance, and technician performance tables. Only accessible to Project Managers.',
    description_es: 'La página de Reportes proporciona análisis del progreso del proyecto. Contiene tarjetas KPI, distribución de estados, rendimiento por zona y tablas de rendimiento de técnicos. Solo accesible para Project Managers.',
    targetSelector: '.reports-title',
    position: 'bottom',
    route: '/reports',
    sidebarAction: 'close',
    group: 'Reports',
    group_es: 'Reportes',
    pmOnly: true,
  },
  {
    id: 'reports-kpis',
    title: 'KPI Cards',
    title_es: 'Tarjetas KPI',
    description: 'Key Performance Indicators: Total Tasks, Completion Rate (%), Average Hours per Task, and On-Time Rate (tasks finished before their end date). These provide a high-level project health summary.',
    description_es: 'Indicadores Clave de Rendimiento: Total Tasks, Completion Rate (%), Average Hours per Task y On-Time Rate (tareas terminadas antes de su fecha fin). Proporcionan un resumen de salud del proyecto.',
    targetSelector: '.reports-kpis',
    position: 'bottom',
    route: '/reports',
    group: 'Reports',
    group_es: 'Reportes',
    pmOnly: true,
  },
  {
    id: 'reports-status',
    title: 'Status Distribution',
    title_es: 'Distribución de Estados',
    description: 'A grid of status cards showing: the count, label, and percentage of tasks in each status (Assigned, Started, In Progress, Paused, Finished, Issues). Each card has a colored top border matching the status color.',
    description_es: 'Una cuadrícula de tarjetas de estado mostrando: conteo, etiqueta y porcentaje de tareas en cada estado (Assigned, Started, In Progress, Paused, Finished, Issues). Cada tarjeta tiene un borde superior coloreado según el estado.',
    targetSelector: '.reports-status-grid',
    position: 'bottom',
    route: '/reports',
    group: 'Reports',
    group_es: 'Reportes',
    pmOnly: true,
  },
  {
    id: 'reports-zones',
    title: 'Zone Performance',
    title_es: 'Rendimiento por Zona',
    description: 'A table showing each zone\'s performance: Zone Name, Total Tasks, Completed Tasks, In Progress Tasks, Issues count, and Completion Rate with an inline progress bar. Helps identify which areas of the project are ahead or behind.',
    description_es: 'Una tabla mostrando el rendimiento de cada zona: Nombre de Zona, Total Tasks, Completed, In Progress, Issues y Completion Rate con barra de progreso. Ayuda a identificar qué áreas del proyecto van adelantadas o atrasadas.',
    targetSelector: '.reports-section:nth-child(3)',
    position: 'top',
    route: '/reports',
    group: 'Reports',
    group_es: 'Reportes',
    pmOnly: true,
  },
  {
    id: 'reports-technicians',
    title: 'Technician Performance',
    title_es: 'Rendimiento de Técnicos',
    description: 'A table showing each technician\'s stats: Name, Total Tasks assigned, Completed, In Progress, Issues, and Completion Rate. Helps the PM evaluate individual performance and redistribute work if needed.',
    description_es: 'Una tabla mostrando estadísticas de cada técnico: Nombre, Total Tasks asignadas, Completed, In Progress, Issues y Completion Rate. Ayuda al PM a evaluar rendimiento individual y redistribuir trabajo si es necesario.',
    targetSelector: '.reports-section:nth-child(4)',
    position: 'top',
    route: '/reports',
    group: 'Reports',
    group_es: 'Reportes',
    pmOnly: true,
  },

  // ========== SETTINGS ==========
  {
    id: 'settings-overview',
    title: 'Settings Page',
    title_es: 'Página de Configuración',
    description: 'Settings manages the reference data used across the application: Projects, User accounts, Appearance (dark/light theme), and System Information. Only Project Managers can modify Projects and Users. Let\'s explore each section in detail.',
    description_es: 'Configuración administra los datos de referencia usados en toda la aplicación: Proyectos, cuentas de Usuario, Apariencia (tema oscuro/claro) e Información del Sistema. Solo los Project Managers pueden modificar Proyectos y Usuarios. Veamos cada sección en detalle.',
    targetSelector: '.settings-title',
    position: 'bottom',
    route: '/settings',
    sidebarAction: 'close',
    group: 'Settings',
    group_es: 'Configuración',
    pmOnly: true,
  },
  {
    id: 'settings-crud-pattern',
    title: 'Shared CRUD Pattern',
    title_es: 'Patrón CRUD Compartido',
    description: 'All settings tables share the same interaction pattern: click "+ Add" to show an inline form, fill fields, save with ✓. Click the pencil icon to edit a row inline. Delete with trash icon (✓ to confirm, ✕ to cancel). Changes sync instantly with the database.',
    description_es: 'Todas las tablas de configuración comparten el mismo patrón de interacción: clic en "+ Add" para mostrar un formulario en línea, completar campos, guardar con ✓. Clic en el ícono de lápiz para editar una fila en línea. Eliminar con ícono de papelera (✓ para confirmar, ✕ para cancelar). Los cambios se sincronizan al instante con la base de datos.',
    targetSelector: '.settings-content',
    position: 'top',
    route: '/settings',
    group: 'Settings',
    group_es: 'Configuración',
    pmOnly: true,
  },

  // ========== PROJECTS SECTION ==========
  {
    id: 'settings-proj-card',
    title: 'Projects Card',
    title_es: 'Tarjeta de Proyectos',
    description: 'The first card manages Projects. Projects organize all the work in the system — Zones, Sections, Elements, Sub-elements and Tasks all belong to a project. Each project has a Code, Name, Client, Status, and Team Members.',
    description_es: 'La primera tarjeta administra Proyectos. Los proyectos organizan todo el trabajo en el sistema — Zonas, Secciones, Elementos, Sub-elementos y Tareas pertenecen a un proyecto. Cada proyecto tiene Código, Nombre, Cliente, Estado y Miembros del Equipo.',
    targetSelector: '.settings-content > .settings-card:nth-child(1)',
    position: 'bottom',
    route: '/settings',
    sidebarAction: 'close',
    group: 'Settings — Projects',
    group_es: 'Configuración — Proyectos',
    pmOnly: true,
  },
  {
    id: 'settings-proj-add-btn',
    title: '"+ Add Project" Button',
    title_es: 'Botón "+ Add Project"',
    description: 'Click "+ Add Project" to open an inline form row at the top of the table. The form will open now.',
    description_es: 'Haz clic en "+ Add Project" para abrir una fila de formulario en línea arriba de la tabla. El formulario se abrirá ahora.',
    targetSelector: '.settings-content > .settings-card:nth-child(1) .add-btn',
    position: 'bottom',
    route: '/settings',
    group: 'Settings — Projects',
    group_es: 'Configuración — Proyectos',
    pmOnly: true,
  },
  {
    id: 'settings-proj-form',
    title: 'Add Project Form',
    title_es: 'Formulario Agregar Proyecto',
    description: 'The inline form has 3 fields: Code (monospace — e.g. "PRJ-001"), Project Name, and Client. Status defaults to "Planning" and Members can be assigned after creation. Click ✓ to save or ✕ to cancel.',
    description_es: 'El formulario en línea tiene 3 campos: Código (monoespaciado — ej. "PRJ-001"), Nombre del Proyecto y Cliente. El Estado por defecto es "Planning" y los Miembros se pueden asignar después de la creación. Clic ✓ para guardar o ✕ para cancelar.',
    targetSelector: '.settings-content > .settings-card:nth-child(1) .new-form-row',
    position: 'bottom',
    route: '/settings',
    waitForSelector: '.settings-content > .settings-card:nth-child(1) .new-form-row',
    group: 'Settings — Projects',
    group_es: 'Configuración — Proyectos',
    pmOnly: true,
  },
  {
    id: 'settings-proj-table',
    title: 'Projects Table',
    title_es: 'Tabla de Proyectos',
    description: 'The table shows: Code (monospace), Name, Client, Status (colored badge — purple "Planning", green "Active", amber "On Hold", blue "Completed"), Members (count), and Actions (pencil to edit, trash to delete). The form will close now.',
    description_es: 'La tabla muestra: Código (monoespaciado), Nombre, Cliente, Estado (badge de color — púrpura "Planning", verde "Active", ámbar "On Hold", azul "Completed"), Miembros (conteo) y Acciones (lápiz para editar, papelera para eliminar). El formulario se cerrará ahora.',
    targetSelector: '.settings-content > .settings-card:nth-child(1) .settings-table',
    position: 'bottom',
    route: '/settings',
    group: 'Settings — Projects',
    group_es: 'Configuración — Proyectos',
    pmOnly: true,
  },
  {
    id: 'settings-proj-members',
    title: 'Team Members (Edit Mode)',
    title_es: 'Miembros del Equipo (Modo Edición)',
    description: 'When editing a project, the Members column shows a multi-select search dropdown. Type to search team members by name, click to toggle assignment. Multiple members can be assigned simultaneously. This controls who sees the project in their sidebar.',
    description_es: 'Al editar un proyecto, la columna de Miembros muestra un dropdown de búsqueda multi-selección. Escribe para buscar miembros por nombre, clic para alternar asignación. Se pueden asignar múltiples miembros simultáneamente. Esto controla quién ve el proyecto en su barra lateral.',
    targetSelector: '.settings-content > .settings-card:nth-child(1) .settings-table',
    position: 'top',
    route: '/settings',
    group: 'Settings — Projects',
    group_es: 'Configuración — Proyectos',
    pmOnly: true,
  },

  // ========== USER MANAGEMENT SECTION ==========
  {
    id: 'settings-user-card',
    title: 'User Management Card',
    title_es: 'Tarjeta de Gestión de Usuarios',
    description: 'The second card manages User accounts. Only Project Managers can edit or delete users. This table lets the PM update each user\'s Name, Role, and Phone.',
    description_es: 'La segunda tarjeta administra cuentas de Usuario. Solo los Project Managers pueden editar o eliminar usuarios. Esta tabla permite al PM actualizar el Nombre, Rol y Teléfono de cada usuario.',
    targetSelector: '.settings-content > .settings-card:nth-child(2)',
    position: 'bottom',
    route: '/settings',
    group: 'Settings — Users',
    group_es: 'Configuración — Usuarios',
    pmOnly: true,
  },
  {
    id: 'settings-user-table',
    title: 'Users Table',
    title_es: 'Tabla de Usuarios',
    description: 'The table shows: Name (with colored avatar circle), Role (colored badge — blue "Project Manager", green "Technician", purple "Helper", amber "Electrician", red "Mechanic"), Email, Phone, Created date, and Actions.',
    description_es: 'La tabla muestra: Nombre (con círculo de avatar coloreado), Rol (badge de color — azul "Project Manager", verde "Technician", púrpura "Helper", ámbar "Electrician", rojo "Mechanic"), Email, Teléfono, fecha de Creación y Acciones.',
    targetSelector: '.settings-content > .settings-card:nth-child(2) .settings-table',
    position: 'top',
    route: '/settings',
    group: 'Settings — Users',
    group_es: 'Configuración — Usuarios',
    pmOnly: true,
  },
  {
    id: 'settings-user-edit',
    title: 'User Editing',
    title_es: 'Edición de Usuario',
    description: 'Click the pencil icon to edit a user inline. Editable fields: Name (text input), Role (dropdown: Project Manager, Technician, Helper, Electrician, Mechanic), and Phone (with international dialing code selector — 🇺🇸+1, 🇲🇽+52, etc.). Email is read-only (shown grayed out). Click ✓ to save or ✕ to cancel.',
    description_es: 'Clic en el ícono de lápiz para editar un usuario en línea. Campos editables: Nombre (input de texto), Rol (dropdown: Project Manager, Technician, Helper, Electrician, Mechanic) y Teléfono (con selector de código internacional — 🇺🇸+1, 🇲🇽+52, etc.). Email es de solo lectura (se muestra grisáceo). Clic ✓ para guardar o ✕ para cancelar.',
    targetSelector: '.settings-content > .settings-card:nth-child(2) .settings-table',
    position: 'bottom',
    route: '/settings',
    group: 'Settings — Users',
    group_es: 'Configuración — Usuarios',
    pmOnly: true,
  },
  {
    id: 'settings-user-email',
    title: 'Email & Auth Details',
    title_es: 'Email y Detalles de Auth',
    description: 'The Email column shows the user\'s login email. Users without an email configured show "No email set", with a "custom" tag if a notification email has been added separately. Regular users show their real email.',
    description_es: 'La columna de Email muestra el email de inicio de sesión del usuario. Los usuarios sin email configurado muestran "No email set", con un tag "custom" si se ha añadido un email de notificación por separado. Los usuarios regulares muestran su email real.',
    targetSelector: '.settings-content > .settings-card:nth-child(2) .settings-table',
    position: 'top',
    route: '/settings',
    group: 'Settings — Users',
    group_es: 'Configuración — Usuarios',
    pmOnly: true,
  },

  // ========== APPEARANCE SECTION ==========
  {
    id: 'settings-appearance',
    title: 'Appearance Card',
    title_es: 'Tarjeta de Apariencia',
    description: 'The Appearance card is visible to all users (not just Project Managers). It controls the visual theme of the entire application.',
    description_es: 'La tarjeta de Apariencia es visible para todos los usuarios (no solo Project Managers). Controla el tema visual de toda la aplicación.',
    targetSelector: '.settings-content > .settings-card:nth-child(3)',
    position: 'bottom',
    route: '/settings',
    group: 'Settings — Appearance',
    group_es: 'Configuración — Apariencia',
    pmOnly: true,
  },
  {
    id: 'settings-theme',
    title: 'Theme Toggle',
    title_es: 'Alternador de Tema',
    description: 'Click the toggle button to switch between Dark Mode (🌙) and Light Mode (☀️). This is the same toggle available in the sidebar, provided here for convenience. The change applies instantly across all pages.',
    description_es: 'Clic en el botón para alternar entre Modo Oscuro (🌙) y Modo Claro (☀️). Es el mismo alternador disponible en la barra lateral, proporcionado aquí por conveniencia. El cambio se aplica instantáneamente en todas las páginas.',
    targetSelector: '.settings-theme-toggle',
    position: 'top',
    route: '/settings',
    group: 'Settings — Appearance',
    group_es: 'Configuración — Apariencia',
    pmOnly: true,
  },

  // ========== SYSTEM INFO SECTION ==========
  {
    id: 'settings-sysinfo',
    title: 'System Information',
    title_es: 'Información del Sistema',
    description: 'The last card shows system metadata: Application name (RAE Project Management), Version (1.0.0), Mode (green "Online" badge or purple "Offline" badge), and current User with their role. This is read-only information for reference.',
    description_es: 'La última tarjeta muestra metadatos del sistema: Nombre de Aplicación (RAE Project Management), Versión (1.0.0), Modo (badge verde "Online" o badge púrpura "Offline") y Usuario actual con su rol. Esta es información de solo lectura para referencia.',
    targetSelector: '.settings-content > .settings-card:nth-child(4)',
    position: 'top',
    route: '/settings',
    group: 'Settings — System',
    group_es: 'Configuración — Sistema',
    pmOnly: true,
  },

  // ========== FINISH ==========
  {
    id: 'finish',
    title: 'Tutorial Complete!',
    title_es: '¡Tutorial Completado!',
    description: 'You\'ve seen every section of the RAE Project Management system including all CRUD operations, task status workflows, time tracking, and collaboration features. Remember: the Workspace is where you manage the project hierarchy, Task Detail is where work happens, and the Dashboard gives you the overview. Happy managing!',
    description_es: '¡Has visto cada sección del sistema RAE Project Management incluyendo todas las operaciones CRUD, flujos de estado de tareas, seguimiento de tiempo y funciones de colaboración. Recuerda: el Workspace es donde gestionas la jerarquía del proyecto, Task Detail es donde se trabaja, y el Dashboard te da la vista general. ¡Feliz gestión!',
    position: 'center',
    group: 'Finish',
    group_es: 'Fin',
    pmOnly: true,
  },
  {
    id: 'finish',
    title: 'Tutorial Complete!',
    title_es: '¡Tutorial Completado!',
    description: 'You\'ve seen all the sections available to you! Use My Tasks to manage your assigned work, change task statuses, track time, and collaborate with your team through comments and attachments. The Calendar gives you a visual overview of the project schedule. Happy working!',
    description_es: '¡Has visto todas las secciones disponibles para ti! Usa Mis Tareas para gestionar tu trabajo asignado, cambiar estados de tareas, registrar tiempo y colaborar con tu equipo mediante comentarios y archivos adjuntos. El Calendario te da una vista visual del cronograma del proyecto. ¡Feliz trabajo!',
    position: 'center',
    group: 'Finish',
    group_es: 'Fin',
    userOnly: true,
  },
];

interface TutorialOverlayProps {
  isActive: boolean;
  onClose: () => void;
  isProjectManager?: boolean;
}

export const TutorialOverlay: React.FC<TutorialOverlayProps> = ({ isActive, onClose, isProjectManager = true }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [highlightStyle, setHighlightStyle] = useState<React.CSSProperties>({});
  const [showHighlight, setShowHighlight] = useState(false);
  const [lang, setLang] = useState<Lang>('en');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const steps = TUTORIAL_STEPS.filter(s => {
    if (isProjectManager) return !s.userOnly;
    return !s.pmOnly;
  });
  const step = steps[currentStep];
  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Reset to step 0 when tutorial opens
  useEffect(() => {
    if (isActive) setCurrentStep(0);
  }, [isActive]);

  const groups = Array.from(new Set(steps.map(s => lang === 'es' ? s.group_es : s.group)));
  const currentGroupIndex = groups.indexOf(lang === 'es' ? step.group_es : step.group);

  const positionTooltip = useCallback(() => {
    if (!step) return;

    if (!step.targetSelector || step.position === 'center') {
      setShowHighlight(false);
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      });
      return;
    }

    const target = document.querySelector(step.targetSelector);
    if (!target) {
      setShowHighlight(false);
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      });
      return;
    }

    const rect = target.getBoundingClientRect();
    const pad = 8;

    setShowHighlight(true);
    setHighlightStyle({
      position: 'fixed',
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
      borderRadius: '8px',
    });

    const tooltipW = 380;
    const tooltipH = 220;
    const pos = step.position || 'bottom';
    let top = 0, left = 0;

    switch (pos) {
      case 'bottom':
        top = rect.bottom + pad + 12;
        left = rect.left + rect.width / 2 - tooltipW / 2;
        break;
      case 'top':
        top = rect.top - pad - tooltipH - 12;
        left = rect.left + rect.width / 2 - tooltipW / 2;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipH / 2;
        left = rect.right + pad + 12;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipH / 2;
        left = rect.left - pad - tooltipW - 12;
        break;
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left < 12) left = 12;
    if (left + tooltipW > vw - 12) left = vw - tooltipW - 12;
    if (top < 12) top = 12;
    if (top + tooltipH > vh - 12) top = vh - tooltipH - 12;

    setTooltipStyle({
      position: 'fixed',
      top,
      left,
    });
  }, [step]);

  useEffect(() => {
    if (!isActive || !step) return;

    const perform = async () => {
      if (step.route && location.pathname !== step.route) {
        navigate(step.route);
        await new Promise(r => setTimeout(r, 300));
      }

      if (step.sidebarAction) {
        const sidebar = document.querySelector('.sidebar');
        const isOpen = sidebar?.classList.contains('open');
        if (step.sidebarAction === 'open' && !isOpen) {
          const btn = document.querySelector('.menu-button') as HTMLElement;
          btn?.click();
          await new Promise(r => setTimeout(r, 350));
        } else if (step.sidebarAction === 'close' && isOpen) {
          const backdrop = document.querySelector('.sidebar-backdrop') as HTMLElement;
          backdrop?.click();
          await new Promise(r => setTimeout(r, 350));
        }
      }

      // Step-specific DOM interactions
      switch (step.id) {
        case 'ws-hierarchy-section': {
          if (!document.querySelector('.ws-row-section')) {
            (document.querySelector('.ws-row-zone') as HTMLElement)?.click();
            await new Promise(r => setTimeout(r, 400));
          }
          break;
        }
        case 'ws-hierarchy-element': {
          if (!document.querySelector('.ws-row-element')) {
            (document.querySelector('.ws-row-section') as HTMLElement)?.click();
            await new Promise(r => setTimeout(r, 400));
          }
          break;
        }
        case 'ws-hierarchy-subelement': {
          if (!document.querySelector('.ws-row-subelement')) {
            (document.querySelector('.ws-row-element') as HTMLElement)?.click();
            await new Promise(r => setTimeout(r, 400));
          }
          break;
        }
        case 'ws-crud-overview': {
          const expandBtns = document.querySelectorAll('.ws-toolbar-btn');
          expandBtns.forEach(btn => {
            if (btn.textContent?.includes('Expand')) (btn as HTMLElement).click();
          });
          await new Promise(r => setTimeout(r, 500));
          break;
        }
        case 'task-detail-enter': {
          if (!document.querySelector('.detail-page')) {
            if (isProjectManager) {
              navigate('/workspace');
              await new Promise(r => setTimeout(r, 400));
              const eBtns = document.querySelectorAll('.ws-toolbar-btn');
              eBtns.forEach(btn => {
                if (btn.textContent?.includes('Expand')) (btn as HTMLElement).click();
              });
              await new Promise(r => setTimeout(r, 500));
              (document.querySelector('.ws-row-subelement') as HTMLElement)?.click();
              await new Promise(r => setTimeout(r, 600));
            } else {
              navigate('/my-tasks');
              await new Promise(r => setTimeout(r, 500));
              (document.querySelector('.mytask-card') as HTMLElement)?.click();
              await new Promise(r => setTimeout(r, 600));
            }
          }
          break;
        }
        case 'calendar-gantt': {
          const viewBtns = document.querySelectorAll('.cal-page-view-btn');
          if (viewBtns[1]) (viewBtns[1] as HTMLElement).click();
          await new Promise(r => setTimeout(r, 500));
          break;
        }
        case 'calendar-gantt-expanded': {
          const gViewBtns = document.querySelectorAll('.cal-page-view-btn');
          if (gViewBtns[1]) (gViewBtns[1] as HTMLElement).click();
          await new Promise(r => setTimeout(r, 300));
          (document.querySelector('.gantt-expand-btn') as HTMLElement)?.click();
          await new Promise(r => setTimeout(r, 500));
          break;
        }

        // ========== SETTINGS DOM INTERACTIONS ==========
        case 'settings-proj-add-btn': {
          const addBtn = document.querySelector('.settings-content > .settings-card:nth-child(1) .add-btn') as HTMLButtonElement;
          if (addBtn) {
            addBtn.click();
            await new Promise(r => setTimeout(r, 300));
          }
          break;
        }
        case 'settings-proj-table': {
          // Close the add form by clicking cancel
          const cancelBtn = document.querySelector('.settings-content > .settings-card:nth-child(1) .new-form-row .action-icon-btn.cancel') as HTMLButtonElement;
          if (cancelBtn) {
            cancelBtn.click();
            await new Promise(r => setTimeout(r, 300));
          }
          break;
        }
      }

      if (step.waitForSelector) {
        let attempts = 0;
        while (!document.querySelector(step.waitForSelector) && attempts < 20) {
          await new Promise(r => setTimeout(r, 100));
          attempts++;
        }
      }

      if (step.targetSelector) {
        const target = document.querySelector(step.targetSelector);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          await new Promise(r => setTimeout(r, 400));
        }
      }

      await new Promise(r => setTimeout(r, 150));
      positionTooltip();
    };

    perform();
  }, [currentStep, isActive, step, navigate, location.pathname, positionTooltip, isProjectManager]);

  useEffect(() => {
    if (!isActive) return;
    const handler = () => positionTooltip();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [isActive, positionTooltip]);

  useEffect(() => {
    if (!isActive) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' || e.key === 'Enter') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const goNext = () => {
    if (currentStep < totalSteps - 1) setCurrentStep(s => s + 1);
    else onClose();
  };

  const goPrev = () => {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  };

  if (!isActive || !step) return null;

  return (
    <div className="tutorial-overlay">
      <div className="tutorial-backdrop" onClick={onClose} />

      {showHighlight && (
        <div className="tutorial-highlight" style={highlightStyle} />
      )}

      <div className="tutorial-tooltip" style={tooltipStyle} ref={tooltipRef}>
        <div className="tutorial-top-bar">
          <button
            className="tutorial-lang-toggle"
            onClick={() => setLang(l => l === 'en' ? 'es' : 'en')}
            title={lang === 'en' ? 'Cambiar a Español' : 'Switch to English'}
          >
            {lang === 'en' ? '🌐 ES' : '🌐 EN'}
          </button>
          <button className="tutorial-close" onClick={onClose} title={lang === 'es' ? 'Salir del tutorial (ESC)' : 'Exit tutorial (ESC)'}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="tutorial-group-badge">{lang === 'es' ? step.group_es : step.group}</div>

        <h3 className="tutorial-title">{lang === 'es' ? step.title_es : step.title}</h3>
        <p className="tutorial-description">{lang === 'es' ? step.description_es : step.description}</p>

        <div className="tutorial-footer">
          <div className="tutorial-progress-section">
            <div className="tutorial-progress-bar">
              <div className="tutorial-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="tutorial-step-counter">{currentStep + 1} / {totalSteps}</span>
          </div>

          <div className="tutorial-nav-buttons">
            <button
              className="tutorial-btn tutorial-btn-prev"
              onClick={goPrev}
              disabled={currentStep === 0}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              {lang === 'es' ? 'Atrás' : 'Back'}
            </button>
            <button className="tutorial-btn tutorial-btn-next" onClick={goNext}>
              {currentStep === totalSteps - 1
                ? (lang === 'es' ? 'Finalizar' : 'Finish')
                : (lang === 'es' ? 'Siguiente' : 'Next')}
              {currentStep < totalSteps - 1 && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="tutorial-group-dots">
          {groups.map((g, i) => (
            <div
              key={g}
              className={`tutorial-group-dot ${i === currentGroupIndex ? 'active' : ''} ${i < currentGroupIndex ? 'completed' : ''}`}
              title={g}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const TUTORIAL_STORAGE_KEY = 'rae_pm_tutorial_completed';
